import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { getToken } from '@/util/localStorage';
import { redirectURL } from '@/util/redirect';

import HomeView from '@/views/HomeView.vue';
import SigninView from '@/views/signin/SigninView.vue';
import ConfigView from '@/views/config/ConfigView.vue';
import NotFoundView from '@/views/NotFoundView.vue';

import HitomiView from '@/views/hitomi/HitomiView.vue';
import HitomiInformationView from '@/views/hitomi/HitomiInformationView.vue';
import CreateHitomiView from '@/views/hitomi/CreateHitomiView.vue';
import UpdateHitomiView from '@/views/hitomi/UpdateHitomiView.vue';

import DoujinView from '@/views/doujin/DoujinView.vue';
import CreateDoujinView from '@/views/doujin/CreateDoujinView.vue';
import DoujinDetailView from '@/views/doujin/DoujinDetailView.vue';
import BulkCreateDoujinView from '@/views/doujin/BulkCreateDoujinView.vue';

import IllustratorView from '@/views/illustration/IllustratorView.vue';
import IllustrationView from '@/views/illustration/IllustrationView.vue';
import IllustrationDetailView from '@/views/illustration/IllustrationDetailView.vue';
import CreateIllustrationView from '@/views/illustration/CreateIllustrationView.vue';
import UpdateIllustrationView from '@/views/illustration/UpdateIllustrationView.vue';

import MangaView from '@/views/manga/MangaView.vue';
import CreateMangaView from '@/views/manga/CreateMangaView.vue';
import UpdateMangaView from '@/views/manga/UpdateMangaView.vue';
import MangaChapterView from '@/views/manga/MangaChapterView.vue';
import MangaDetailView from '@/views/manga/MangaDetailView.vue';

import ManhwaView from '@/views/manhwa/ManhwaView.vue';
import CreateManhwaView from '@/views/manhwa/CreateManhwaView.vue';
import UpdateManhwaView from '@/views/manhwa/UpdateManhwaView.vue';
import ManhwaChapterView from '@/views/manhwa/ManhwaChapterView.vue';
import ManhwaDetailView from '@/views/manhwa/ManhwaDetailView.vue';

import JAVView from '@/views/jav/JAVView.vue';
import UpdateJAVView from '@/views/jav/UpdateJAVView.vue';
import CreateJAVView from '@/views/jav/CreateJAVView.vue';

import HentaiView from '@/views/hentai/HentaiView.vue';
import HentaiDetailView from '@/views/hentai/HentaiDetailView.vue';
import CreateHentaiView from '@/views/hentai/CreateHentaiView.vue';
import UpdateHentaiView from '@/views/hentai/UpdateHentaiView.vue';

import OnlyfansView from '@/views/onlyfans/OnlyfansView.vue';
import OnlyfansActorView from '@/views/onlyfans/OnlyfansActorView.vue';
import CreateOnlyfansView from '@/views/onlyfans/CreateOnlyfansView.vue';
import UpdateOnlyfansView from '@/views/onlyfans/UpdateOnlyfansView.vue';
import OnlyfansDetailView from '@/views/onlyfans/OnlyfansDetailView.vue';

import AnimeView from '@/views/anime/AnimeView.vue';
import NovelView from '@/views/novel/NovelView.vue';
import VisualNovelView from '@/views/visual-novel/VisualNovelView.vue';
import HitomiReaderView from '@/views/hitomi/HitomiReaderView.vue';
import UpdateNovelView from '@/views/novel/UpdateNovelView.vue';
import CreateNovelView from '@/views/novel/CreateNovelView.vue';
import NovelChapterView from '@/views/novel/NovelChapterView.vue';
import NovelDetailView from '@/views/novel/NovelDetailView.vue';

const appTitle = 'Unikume Kinkando';

const routes: Array<RouteRecordRaw> = [
	// Illustration
	{
		path: '/illustration',
		name: 'Illustration',
		component: IllustratorView,
		meta: {
			title: `Illustration | ${appTitle}`
		}
	},
	{
		path: '/illustration/:authorID',
		name: 'Illustration of Illustrator',
		component: IllustrationView,
		meta: {
			title: `{authorID} - Illustration | ${appTitle}`
		}
	},
	{
		path: '/illustration/:authorID/:documentUUID',
		name: 'Illustration Detail',
		component: IllustrationDetailView,
		meta: {
			title: `{authorID} - Illustration | ${appTitle}`,
			hideBreadcrumbPath: [':documentUUID']
		}
	},
	{
		path: '/illustration/:authorID/create',
		name: 'Create Illustration',
		component: CreateIllustrationView,
		meta: {
			title: `{authorID} - Create Illustration | ${appTitle}`
		}
	},
	{
		path: '/illustration/:authorID/update/:documentUUID',
		name: 'Update Illustration',
		component: UpdateIllustrationView,
		meta: {
			title: `{authorID} - Update Illustration | ${appTitle}`,
			hideBreadcrumbPath: [':documentUUID']
		}
	},

	// Hitomi
	{
		path: '/hitomi',
		name: 'Hitomi',
		component: HitomiView,
		meta: {
			title: `Hitomi | ${appTitle}`
		}
	},
	{
		path: '/hitomi/:hitomiUUID',
		name: 'Hitomi Information',
		component: HitomiInformationView,
		meta: {
			title: `Hitomi Information | ${appTitle}`,
			hideBreadcrumbPath: [':hitomiUUID']
		}
	},
	{
		path: '/hitomi/reader/:hitomiUUID/:code',
		name: 'Hitomi Reader',
		component: HitomiReaderView,
		meta: {
			title: `Hitomi Reader | ${appTitle}`,
			hideBreadcrumbPath: [':hitomiUUID', ':code', 'reader']
		}
	},
	{
		path: '/hitomi/create',
		name: 'Create Hitomi',
		component: CreateHitomiView,
		meta: {
			title: `Create Hitomi | ${appTitle}`
		}
	},
	{
		path: '/hitomi/update/:hitomiUUID',
		name: 'Update Hitomi',
		component: UpdateHitomiView,
		meta: {
			title: `Update Hitomi | ${appTitle}`,
			hideBreadcrumbPath: [':hitomiUUID']
		}
	},

	// Manhwa
	{
		path: '/manhwa',
		name: 'Manhwa',
		component: ManhwaView,
		meta: {
			title: `Manhwa | ${appTitle}`
		}
	},
	{
		path: '/manhwa/:comicUUID',
		name: 'Manhwa Detail',
		component: ManhwaDetailView,
		meta: {
			title: `Manhwa | ${appTitle}`,
			hideBreadcrumbPath: [':comicUUID']
		}
	},
	{
		path: '/manhwa/:comicUUID/:chapter',
		name: 'Manhwa Chapter',
		component: ManhwaChapterView,
		meta: {
			title: `Chapter {chapter} - Manhwa | ${appTitle}`,
			hideBreadcrumbPath: [':comicUUID', ':chapter']
		}
	},
	{
		path: '/manhwa/create',
		name: 'Create Manhwa',
		component: CreateManhwaView,
		meta: {
			title: `Create Manhwa | ${appTitle}`
		}
	},
	{
		path: '/manhwa/update/:comicUUID',
		name: 'Update Manhwa',
		component: UpdateManhwaView,
		meta: {
			title: `Update Manhwa | ${appTitle}`,
			hideBreadcrumbPath: [':comicUUID']
		}
	},

	// Doujin
	{
		path: '/doujin',
		name: 'Doujin',
		component: DoujinView,
		meta: {
			title: `Doujin | ${appTitle}`
		}
	},
	{
		path: '/doujin/create',
		name: 'Create Doujin',
		component: CreateDoujinView,
		meta: {
			title: `Create Doujin | ${appTitle}`
		}
	},
	{
		path: '/doujin/create/bulk',
		name: 'Bulk Create Doujin',
		component: BulkCreateDoujinView,
		meta: {
			title: `Bulk Create Doujin | ${appTitle}`
		}
	},
	{
		path: '/doujin/:doujinUUID',
		name: 'Doujin Detail',
		component: DoujinDetailView,
		meta: {
			title: `Doujin | ${appTitle}`,
			hideBreadcrumbPath: [':doujinUUID']
		}
	},

	// ONLYFANS
	{
		path: '/onlyfans',
		name: 'Onlyfans',
		component: OnlyfansActorView,
		meta: {
			title: `Onlyfans | ${appTitle}`
		}
	},
	{
		path: '/onlyfans/:actorID',
		name: 'Onlyfans of Actor',
		component: OnlyfansView,
		meta: {
			title: `{actorID} - Onlyfans | ${appTitle}`
		}
	},
	{
		path: '/onlyfans/:actorID/create',
		name: 'Create Onlyfans',
		component: CreateOnlyfansView,
		meta: {
			title: `{actorID} - Create Onlyfans | ${appTitle}`
		}
	},
	{
		path: '/onlyfans/:actorID/update/:documentUUID',
		name: 'Update Onlyfans',
		component: UpdateOnlyfansView,
		meta: {
			title: `{actorID} - Update Onlyfans | ${appTitle}`,
			hideBreadcrumbPath: [':documentUUID']
		}
	},
	{
		path: '/onlyfans/:actorID/:documentUUID',
		name: 'Onlyfans Detail',
		component: OnlyfansDetailView,
		meta: {
			title: `{actorID} - Onlyfans | ${appTitle}`,
			hideBreadcrumbPath: [':documentUUID']
		}
	},

	// JAV
	{
		path: '/jav',
		name: 'JAV',
		component: JAVView,
		meta: {
			title: `JAV | ${appTitle}`
		}
	},
	{
		path: '/jav/create',
		name: 'Create JAV',
		component: CreateJAVView,
		meta: {
			title: `Create JAV | ${appTitle}`
		}
	},
	{
		path: '/jav/update/:javUUID',
		name: 'Update JAV',
		component: UpdateJAVView,
		meta: {
			title: `Update JAV | ${appTitle}`,
			hideBreadcrumbPath: [':javUUID']
		}
	},

	// Anime
	{
		path: '/anime',
		name: 'Anime',
		component: AnimeView,
		meta: {
			title: `Anime | ${appTitle}`
		}
	},

	// Manga
	{
		path: '/manga',
		name: 'Manga',
		component: MangaView,
		meta: {
			title: `Manga | ${appTitle}`
		}
	},
	{
		path: '/manga/:comicUUID',
		name: 'Manga Detail',
		component: MangaDetailView,
		meta: {
			title: `Manga | ${appTitle}`,
			hideBreadcrumbPath: [':comicUUID']
		}
	},
	{
		path: '/manga/:comicUUID/:chapter',
		name: 'Manga Chapter',
		component: MangaChapterView,
		meta: {
			title: `Chapter {chapter} - Manga | ${appTitle}`,
			hideBreadcrumbPath: [':comicUUID', ':chapter']
		}
	},
	{
		path: '/manga/create',
		name: 'Create Manga',
		component: CreateMangaView,
		meta: {
			title: `Create Manga | ${appTitle}`
		}
	},
	{
		path: '/manga/update/:comicUUID',
		name: 'Update Manga',
		component: UpdateMangaView,
		meta: {
			title: `Update Manga | ${appTitle}`,
			hideBreadcrumbPath: [':comicUUID']
		}
	},

	// Hentai
	{
		path: '/hentai',
		name: 'Hentai',
		component: HentaiView,
		meta: {
			title: `Hentai | ${appTitle}`
		}
	},
	{
		path: '/hentai/:hentaiUUID',
		name: 'Hentai Detail',
		component: HentaiDetailView,
		meta: {
			title: `Hentai | ${appTitle}`,
			hideBreadcrumbPath: [':hentaiUUID']
		}
	},
	{
		path: '/hentai/create',
		name: 'Create Hentai',
		component: CreateHentaiView,
		meta: {
			title: `Create Hentai | ${appTitle}`
		}
	},
	{
		path: '/hentai/update/:hentaiUUID',
		name: 'Update Hentai',
		component: UpdateHentaiView,
		meta: {
			title: `Update Hentai | ${appTitle}`,
			hideBreadcrumbPath: [':hentaiUUID']
		}
	},

	// Novel
	{
		path: '/novel',
		name: 'Novel',
		component: NovelView,
		meta: {
			title: `Novel | ${appTitle}`
		}
	},
	{
		path: '/novel/:comicUUID',
		name: 'Novel Detail',
		component: NovelDetailView,
		meta: {
			title: `Novel | ${appTitle}`,
			hideBreadcrumbPath: [':comicUUID']
		}
	},
	{
		path: '/novel/:comicUUID/:chapter',
		name: 'Novel Chapter',
		component: NovelChapterView,
		meta: {
			title: `Chapter {chapter} - Novel | ${appTitle}`,
			hideBreadcrumbPath: [':comicUUID', ':chapter']
		}
	},
	{
		path: '/novel/create',
		name: 'Create Novel',
		component: CreateNovelView,
		meta: {
			title: `Create Novel | ${appTitle}`
		}
	},
	{
		path: '/novel/update/:comicUUID',
		name: 'Update Novel',
		component: UpdateNovelView,
		meta: {
			title: `Update Novel | ${appTitle}`,
			hideBreadcrumbPath: [':comicUUID']
		}
	},

	// VisualNovel
	{
		path: '/visual-novel',
		name: 'VisualNovel',
		component: VisualNovelView,
		meta: {
			title: `Visual Novel | ${appTitle}`
		}
	},

	// Config
	{
		path: '/config',
		name: 'Config',
		component: ConfigView,
		meta: {
			title: `Config | ${appTitle}`
		}
	},

	// Common
	{
		path: '/',
		name: 'Home',
		component: HomeView,
		meta: {
			title: `Home | ${appTitle}`
		}
	},
	{
		path: '/signin',
		name: 'Signin',
		component: SigninView,
		meta: {
			title: `Signin | ${appTitle}`
		}
	},
	{
		path: '/:pathMatch(.*)*',
		component: NotFoundView,
		meta: {
			title: `Page Not Found | ${appTitle}`
		}
	}
];

const router = createRouter({
	history: createWebHistory(process.env.BASE_URL),
	routes
});

router.beforeEach((to, from, next) => {
	const token = getToken();
	let title = String(to.meta.title) || appTitle;
	for (const params of Object.keys(to.params)) {
		title = title.replace(`{${params}}`, to.params[params] as string);
	}
	document.title = title;
	if (to.name !== 'Signin' && (!token || !token.accessToken)) {
		document.title = getTitleFromRouteName('Signin');
		next(`/signin${redirectURL()}`);
	} else if (to.name === 'Signin' && token?.accessToken) {
		document.title = getTitleFromRouteName('Home');
		next({ name: 'Home' });
	} else {
		next();
	}
});

function getTitleFromRouteName(name: string): string {
	const route = routes.find((route) => route.name === name);
	if (route) {
		return String(route?.meta?.title) || appTitle;
	}
	return appTitle;
}

export default router;
